import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import './EntityJoinRequest.css';

const EntityJoinRequest = ({ user, onSuccess }) => {
  const { t } = useTranslation();
  const [requestType, setRequestType] = useState('join_existing');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [requestedRole, setRequestedRole] = useState('user');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Rechercher des organisations
  useEffect(() => {
    if (requestType === 'join_existing') {
      searchOrganizations();
    }
  }, [requestType, searchQuery]);

  const searchOrganizations = async () => {
    try {
      const response = await apiClient.get(`/api/entity-join/organizations/search?q=${searchQuery}`);
      if (response.data.success) {
        setOrganizations(response.data.organizations);
      }
    } catch (error) {
      console.error(t('entityJoinRequest.errors.searchOrganizations'), error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const requestData = {
        requestType,
        ...(requestType === 'join_existing' && { 
          organizationId: selectedOrgId,
          requestedRole 
        }),
        ...(requestType === 'create_organization' && { 
          organizationName: orgName,
          organizationDescription: orgDescription 
        })
      };

      const response = await apiClient.post('/api/entity-join/request', requestData);
      
      if (response.data.success) {
        setSuccess(response.data.message);
        // Reset form
        setSelectedOrgId('');
        setOrgName('');
        setOrgDescription('');
        setRequestedRole('user');
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      setError(error.response?.data?.message || t('entityJoinRequest.errors.createRequest'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="entity-join-request">
      <h3>{t('entityJoinRequest.title')}</h3>
      
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        {/* Type de demande */}
        <div className="form-group">
          <label>{t('entityJoinRequest.form.requestType.label')}</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                value="join_existing"
                checked={requestType === 'join_existing'}
                onChange={(e) => setRequestType(e.target.value)}
              />
              {t('entityJoinRequest.form.requestType.joinExisting')}
            </label>
            <label>
              <input
                type="radio"
                value="create_organization"
                checked={requestType === 'create_organization'}
                onChange={(e) => setRequestType(e.target.value)}
              />
              {t('entityJoinRequest.form.requestType.createOrganization')}
            </label>
          </div>
        </div>

        {/* Rejoindre une organisation existante */}
        {requestType === 'join_existing' && (
          <>
            <div className="form-group">
              <label htmlFor="search-org">{t('entityJoinRequest.form.search.label')}</label>
              <input
                id="search-org"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('entityJoinRequest.form.search.placeholder')}
                className="form-control"
              />
            </div>

            {organizations.length > 0 && (
              <div className="form-group">
                <label htmlFor="select-org">{t('entityJoinRequest.form.selectOrg.label')}</label>
                <select
                  id="select-org"
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="form-control"
                  required
                >
                  <option value="">{t('entityJoinRequest.form.selectOrg.placeholder')}</option>
                  {organizations.map(org => (
                    <option key={org._id} value={org._id}>
                      {org.name} {org.description && `- ${org.description}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="requested-role">{t('entityJoinRequest.form.role.label')}</label>
              <select
                id="requested-role"
                value={requestedRole}
                onChange={(e) => setRequestedRole(e.target.value)}
                className="form-control"
              >
                <option value="user">{t('entityJoinRequest.form.role.user')}</option>
                <option value="analyst">{t('entityJoinRequest.form.role.analyst')}</option>
              </select>
              <small className="help-text">
                {t('entityJoinRequest.form.role.adminNote')}
              </small>
            </div>
          </>
        )}

        {/* Créer une nouvelle organisation */}
        {requestType === 'create_organization' && (
          <>
            <div className="form-group">
              <label htmlFor="org-name">{t('entityJoinRequest.form.orgName.label')}</label>
              <input
                id="org-name"
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder={t('entityJoinRequest.form.orgName.placeholder')}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="org-description">{t('entityJoinRequest.form.orgDescription.label')}</label>
              <textarea
                id="org-description"
                value={orgDescription}
                onChange={(e) => setOrgDescription(e.target.value)}
                placeholder={t('entityJoinRequest.form.orgDescription.placeholder')}
                className="form-control"
                rows="3"
              />
            </div>

            <div className="info-box">
              <p>ℹ️ <strong>{t('entityJoinRequest.form.createInfo.title')}</strong></p>
              <p>{t('entityJoinRequest.form.createInfo.message')}</p>
            </div>
          </>
        )}

        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={loading || (requestType === 'join_existing' && !selectedOrgId)}
        >
          {loading ? t('entityJoinRequest.form.submit.sending') : t('entityJoinRequest.form.submit.send')}
        </button>
      </form>
    </div>
  );
};

export default EntityJoinRequest;
